import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AdminNav } from "@/components/admin/AdminNav";
import { useApi } from "@/lib/api";
import { useMe } from "@/lib/me";
import { rm, tarikh } from "@/pages/AkaunPage";

const MODUL = [["hte", "HTE", "var(--q)"], ["bdq", "BDQ", "var(--v)"], ["skda", "SKDA", "var(--k)"], ["kbm", "KBM", "var(--i)"], ["adab", "ADAB", "var(--mute)"]];
const PRODUK = { payg: "Per Use", asas50: "50 carian", tanpahad: "Tanpa had" };

const Card = ({ t, v, sub, id, to, warn }) => {
  const body = (
    <div className={`surface rounded-xl border p-5 h-full transition-colors ${warn ? "border-[var(--warn,#d92d20)]" : "border-line"} ${to ? "hover:border-[var(--q)]" : ""}`} data-testid={id}>
      <div className="eyebrow">{t}</div>
      <div className="mt-2 font-display text-3xl text-ink">{v}</div>
      {sub && <div className="text-xs text-mute mt-1.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

const Trend = ({ now, prev }) => {
  if (!prev) return <span>Bulan lepas (tempoh sama): {rm(0)}</span>;
  const pct = Math.round(((now - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 ${up ? "text-i" : "text-warn"}`} data-testid="dash-revenue-trend">
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{up ? "+" : ""}{pct}% berbanding {rm(prev)} (tempoh sama bulan lepas)
    </span>
  );
};

const UsageChart = ({ data }) => (
  <div className="surface rounded-xl border border-line p-5" data-testid="dash-usage-chart">
    <div className="eyebrow mb-4">Carian harian · 30 hari terakhir</div>
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data.map((d) => ({ ...d, label: d.tarikh.slice(8) + "/" + d.tarikh.slice(5, 7) }))}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--mute)" interval={2} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--mute)" />
          <Tooltip />
          <Legend />
          {MODUL.map(([k, l, c]) => <Area key={k} type="monotone" dataKey={k} name={l} stackId="a" stroke={c} fill={c} fillOpacity={0.25} />)}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Expiring = ({ items }) => (
  <div className="surface rounded-xl border border-line p-5" data-testid="dash-expiring">
    <div className="eyebrow mb-3">Langganan tamat dalam 7 hari ({items.length})</div>
    {items.length === 0 ? <p className="text-sm text-mute">Tiada.</p> : (
      <ul className="divide-y divide-line">
        {items.map((s) => (
          <li key={s.user_id} className="py-2 flex justify-between gap-3 text-sm" data-testid={`dash-expiring-${s.user_id}`}>
            <span className="truncate">{s.email || s.user_id}</span>
            <span className="text-mute whitespace-nowrap">{s.nama_plan} · {tarikh(s.plan_expires_at)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const TemplateStats = ({ items }) => (
  <div className="surface rounded-xl border border-line p-5" data-testid="dash-template-stats">
    <div className="eyebrow mb-3">Templat Rasmi paling digunakan</div>
    {items.length === 0 ? <p className="text-sm text-mute">Belum ada Templat Rasmi.</p> : (
      <ul className="divide-y divide-line">
        {items.map((t) => (
          <li key={t.id} className="py-2 flex justify-between gap-3 text-sm" data-testid={`dash-template-${t.id}`}>
            <span className="truncate">{t.tajuk}</span>
            <span className="text-mute whitespace-nowrap font-mono-data">{t.program} program · {t.trainer} trainer</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Recent = ({ items }) => (
  <div className="surface rounded-xl border border-line p-5" data-testid="dash-recent-payments">
    <div className="flex justify-between items-baseline mb-3">
      <div className="eyebrow">Bayaran terkini</div>
      <Link to="/admin/bil" className="text-xs text-q underline underline-offset-4" data-testid="dash-all-payments-link">Lihat semua</Link>
    </div>
    {items.length === 0 ? <p className="text-sm text-mute">Belum ada bayaran sebenar.</p> : (
      <ul className="divide-y divide-line">
        {items.map((p) => (
          <li key={p.id} className="py-2 flex justify-between gap-3 text-sm" data-testid={`dash-payment-${p.id}`}>
            <span className="truncate">{p.email || "—"}<span className="block text-xs text-mute">{PRODUK[p.produk]}{p.produk === "payg" && ` ×${p.qty}`}{p.manual_by && " · manual"} · {tarikh(p.paid_at)}</span></span>
            <span className="font-mono-data whitespace-nowrap">{rm(p.amount_sen)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function AdminDashboardPage() {
  const api = useApi();
  const me = useMe();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { if (me?.role === "superadmin") api("get", "/admin/dashboard").then(setD).catch(() => setErr(true)); }, [api, me]);
  if (me && me.role !== "superadmin") return <Navigate to="/" replace />;
  if (err) return <div className="p-10 text-sm text-warn inline-flex gap-2" data-testid="dash-error"><AlertTriangle size={16} /> Gagal memuatkan dashboard. Cuba muat semula.</div>;
  if (!me || !d) return <div className="p-10"><Loader2 className="animate-spin text-q" /></div>;
  const h = d.hasil;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-8" data-testid="admin-dashboard-page">
      <header>
        <AdminNav />
        <div className="eyebrow mt-6">Admin · superadmin</div>
        <h1 className="font-display text-4xl sm:text-5xl text-ink mt-3">Dashboard</h1>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card id="dash-revenue" t="Hasil bulan ini" v={rm(h.bulan_ini.sen)} sub={<Trend now={h.bulan_ini.sen} prev={h.bulan_lepas_setakat.sen} />} to="/admin/bil" />
        <Card id="dash-users" t="Jumlah pengguna" v={d.pengguna.jumlah} sub={`+${d.pengguna.baru_bulan_ini} bulan ini`} />
        <Card id="dash-active-subs" t="Langganan aktif" v={d.langganan_aktif.asas50 + d.langganan_aktif.tanpahad} sub={`50 carian: ${d.langganan_aktif.asas50} · Tanpa had: ${d.langganan_aktif.tanpahad}`} />
        <Card id="dash-unmatched" t="Bayaran belum diproses" v={d.belum_diproses} sub={d.belum_diproses ? "Klik untuk semak" : "Semua sudah diproses"} to="/admin/bil?tab=unmatched" warn={d.belum_diproses > 0} />
        <Card id="dash-expiring-count" t="Tamat dalam 7 hari" v={d.akan_tamat.length} sub="Peringatan emel dihantar 3 hari sebelum" />
        <Card id="dash-searches" t="Carian bulan ini" v={d.carian_bulan_ini} sub="Semua modul, tidak termasuk eksport" />
        <Card id="dash-ai-cost" t="Anggaran kos AI bulan ini" v={`US$${d.kos_ai_usd_bulan_ini.toFixed(2)}`} sub="Anggaran berdasarkan token" />
        <Card id="dash-revenue-last" t="Hasil bulan lepas" v={rm(h.bulan_lepas.sen)} sub={`${h.bulan_lepas.bil} transaksi`} />
      </div>
      <UsageChart data={d.graf_30_hari} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Recent items={d.bayaran_terkini} />
        <Expiring items={d.akan_tamat} />
        <TemplateStats items={d.templat_rasmi || []} />
      </div>
    </div>
  );
}
